import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';

import {
  GET_CLUBS,
  GET_PASSES
} from '../constants/api-endpoints';
import { ShowCatalogService } from '../../shared/show-portal/services/show-catalog.service';
import { ClubDetails } from '../../models/common/club';
import { ClubInfo, GetClubsRequest } from '../../models/common/family-profile';
import { Division } from '../../features/educator/shared/models/division';
import { Class } from '../../features/educator/shared/models/division';
import { Pass } from '../../features/educator/shared/models/pass';
import { toCamelCase } from '../../shared/utils/case-normalizer';
import { parseGetPasses } from '../../shared/show-portal/models/get-passes.mapper';
import { toPagingBody } from '../../shared/show-portal/utils/paging.util';
import { PassType } from '../constants/pass-type.enum';

/** Session key for the signed-in org. Duplicated from `BaseSessionService`
 *  rather than imported: that service imports this one. */
const ORG_ABBREVIATION_KEY = 'org-abbreviation';

/**
 * A cached class/pass row's display name.
 *
 * `GetShowsDivisions` builds its rows from the stored procedure's own columns,
 * where the name lives in `Class` (or `Pass`) and `Description` is frequently
 * empty — so reading `description` alone returned '' and every caller fell back
 * to printing the raw uuid. Same precedence the entry wizards use when they
 * read the very same rows: description, then the name column.
 *
 * @param nameKey The row's own name column — `class` or `pass`.
 */
function cachedName(row: unknown, nameKey: 'class' | 'pass'): string {
  if (!row || typeof row !== 'object') {
    return '';
  }
  const fields = row as Record<string, unknown>;
  const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  return text(fields['description']) || text(fields[nameKey]) || text(fields['name']);
}

export interface ClubFlagFilter {
  isFourHFlag?: boolean;
  isFFAFlag?: boolean;
  isAstCeaFlag?: boolean;
}

export interface OrgConfigs {
  orgId: number;
  orgName: string;
  abbreviation: string;
  loginTypes: Array<{
    loginType: string;
    isEnabled: boolean;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private readonly dbName = 'TLS-SA';
  /** v5: adds the line-item field store used for Additional-Info labels.
   *  Bumping this is what triggers the store rebuild below. */
  private readonly dbVersion = 5;
  private readonly clubsStoreName = 'clubs';
  private readonly passesStoreName = 'passes';
  private readonly classesStoreName = 'classes';
  private readonly divisionsStoreName = 'divisions';
  private readonly orgConfigsStoreName = 'orgConfigs';
  /** Field definitions behind an entry's Additional Information. */
  private readonly lineItemFieldsStoreName = 'lineItemFields';

  private db: IDBDatabase | null = null;

  /** Fetches the division/class catalogue this seed is built from. */
  private readonly catalog = inject(ShowCatalogService);

  private readonly _isInitializedChangeSource = new Subject<boolean>();
  public isInitializedChangeSource$ = this._isInitializedChangeSource.asObservable();

  /**
   * Whether the master data has been cached this session.
   *
   * The subject above only reaches subscribers already listening when it fires,
   * and it fires once, at login — so a screen opened later learns nothing from
   * it and would wait out its own timeout. This is the same answer, readable at
   * any point afterwards.
   */
  isInitialized = false;

  constructor(private readonly http: HttpClient) {}

  /** Show admin signs in through the same `/v1/{show}/Login/...` OTP flow as
   *  every other portal now, so its master data comes from the same show-scoped
   *  endpoints — no separate admin-API path needed. */
  /** In-flight/settled one-shot re-seeds, so a miss refetches at most once. */
  private divisionReseed?: Promise<void>;
  private passReseed?: Promise<void>;
  private clubReseed?: Promise<void>;
  /** The login-time club load, so readers right after login wait for it
   *  instead of finding the store empty and fetching again. */
  private clubsLoad?: Promise<void>;

  private currentShow(): string {
    return sessionStorage.getItem(ORG_ABBREVIATION_KEY) || 'sa';
  }

  /**
   * Open the database and refresh the cached master data. Call once per login.
   *
   * The loaders run concurrently and settle independently: the passes endpoint
   * is not deployed on every show, and a 404 there must not stop clubs,
   * divisions and classes from populating. The subject reports whether the
   * store is usable, so partial data still counts as initialised.
   */
  initializeAfterLogin(): void {
    // A new login may be a different show, so any earlier one-shot re-seed no
    // longer stands for the data now being loaded.
    this.divisionReseed = undefined;
    this.passReseed = undefined;
    this.clubReseed = undefined;
    // Login is the catalogue's refresh point: the seed below makes the one
    // fresh call that every other entry-less caller then reads from.
    this.catalog.clearCache();

    const cleared = this.initDB().then(() => this.clearAllData());
    this.clubsLoad = cleared.then(() => this.loadAllClubDetails()).catch(() => undefined);

    cleared
      .then(() =>
        Promise.allSettled([
          this.clubsLoad,
          this.loadAllPasses(),
          this.loadAllDivisionsAndClasses()
        ])
      )
      .then(() => {
        this.isInitialized = true;
        this._isInitializedChangeSource.next(true);
      })
      .catch(() => {
        // Only a failure to open or clear the database lands here.
        this.isInitialized = false;
        this._isInitializedChangeSource.next(false);
      });
  }

  private initDB(): Promise<void> {
    if (this.db) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Clubs are keyed by clubUuid, straight from GetClubs.
        if (!db.objectStoreNames.contains(this.clubsStoreName)) {
          db.createObjectStore(this.clubsStoreName, { keyPath: 'clubUuid' });
        }

        if (!db.objectStoreNames.contains(this.passesStoreName)) {
          db.createObjectStore(this.passesStoreName, { keyPath: 'passUuid' });
        }

        if (!db.objectStoreNames.contains(this.orgConfigsStoreName)) {
          db.createObjectStore(this.orgConfigsStoreName, { keyPath: 'orgId' });
        }

        // Divisions and classes are keyed by their uuids, exactly like clubs
        // and passes, so a record is looked up directly by the uuid the rest of
        // the app already holds. A key path cannot be altered in place, so any
        // earlier shape is dropped and rebuilt.
        if (event.oldVersion < 3) {
          if (db.objectStoreNames.contains(this.divisionsStoreName)) {
            db.deleteObjectStore(this.divisionsStoreName);
          }
          db.createObjectStore(this.divisionsStoreName, { keyPath: 'divisionUuid' });

          if (db.objectStoreNames.contains(this.classesStoreName)) {
            db.deleteObjectStore(this.classesStoreName);
          }
          db.createObjectStore(this.classesStoreName, { keyPath: 'classUuid' });
        }

        // Field definitions for Additional Information. Keyed by `id`, the only
        // unique column: a settingsId recurs for every division/class it is
        // configured on, so keying by it would drop all but the last row.
        if (event.oldVersion < 5 && db.objectStoreNames.contains(this.lineItemFieldsStoreName)) {
          db.deleteObjectStore(this.lineItemFieldsStoreName);
        }
        if (!db.objectStoreNames.contains(this.lineItemFieldsStoreName)) {
          db.createObjectStore(this.lineItemFieldsStoreName, { keyPath: 'id' })
            .createIndex('settingsId', 'settingsId', { unique: false });
        }

        // Resolving here would race the upgrade transaction; onsuccess fires
        // once it commits and is the only place `this.db` is assigned.
      };
    });
  }

  /**
   * Clear all data from IndexedDB - call this on logout or before loading fresh data
   */
  private clearAllData(): Promise<void> {
    if (!this.db) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const db = this.db!;
      const storeNames = Array.from(db.objectStoreNames);
      const transaction = db.transaction(storeNames, 'readwrite');

      storeNames.forEach((storeName) => {
        const store = transaction.objectStore(storeName);
        store.clear();
      });

      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        resolve();
      };
    });
  }

  /**
   * Ensure database connection exists
   */
  private async ensureDbConnection(): Promise<void> {
    if (!this.db) {
      await this.initDB();
    }
  }

  private storeObject(object: any, storeName: string): Promise<void> {
    if (!this.db) {
      return this.initDB().then(() => this.storeObject(object, storeName));
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const timestamp = Date.now();

      if (!object) {
        resolve();
        return;
      }

      const normalizedObj = this.normalizeUuidKeys(object, storeName);
      const objWithTimestamp = { ...normalizedObj, timestamp };
      const request = store.put(objWithTimestamp);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private storeObjects(objects: any[], storeName: string): Promise<void> {
    if (!this.db) {
      return this.initDB().then(() => this.storeObjects(objects, storeName));
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const timestamp = Date.now();
      let completed = 0;

      if (objects.length === 0) {
        resolve();
        return;
      }

      objects.forEach((obj) => {
        const normalizedObj = this.normalizeUuidKeys(obj, storeName);
        const objWithTimestamp = { ...normalizedObj, timestamp };
        const request = store.put(objWithTimestamp);

        request.onsuccess = () => {
          completed++;
          if (completed === objects.length) {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });
    });
  }

  /** Populate the clubs store from GetClubs, keyed by clubUuid. */
  /** @param replace Clear the store once the rows arrive — a refresh, so
   *  deleted clubs drop out without leaving readers an empty store meanwhile. */
  private loadAllClubDetails(replace = false): Promise<void> {
    return new Promise((resolve, reject) => {
      // Each flag narrows the result, so leaving them all false returns every
      // club — which is what a master-data cache wants.
      const request: GetClubsRequest = {
        clubName: null,
        isAstCeaFlag: false,
        isFourHFlag: false,
        isFFAFlag: false,
        ...toPagingBody({ includeAll: true, sortBy: 'clubName', sortDirection: 'asc' })
      };

      this.http.post<any>(GET_CLUBS(this.currentShow()), request).subscribe({
        next: (response) => {
          // GetClubs answers with a PagedResult inside the BaseResponse
          // envelope, so the rows are under data.items — not data itself.
          const clubs: ClubDetails[] = response?.data?.items ?? [];

          (replace && clubs.length ? this.clearStore(this.clubsStoreName) : Promise.resolve())
            .then(() => this.storeObjects(clubs, this.clubsStoreName))
            .then(() => resolve())
            .catch(reject);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  private async loadAllPasses(): Promise<void> {
    // Passes are show-scoped, so cache the signed-in user's show rather than
    // always San Antonio's — otherwise getPassNameById misses every uuid.
    const show = this.currentShow();

    // Load all three pass types (exhibitor, educator, premium) so the cache
    // can resolve names for any pass entry type. Educators need all types,
    // and family users will only receive exhibitor passes due to role filtering.
    const passTypes = [PassType.EXHIBITOR, PassType.EDUCATOR, PassType.PREMIUM];

    const results = await Promise.all(
      passTypes.map((type) => {
        const payload = {
          ...toPagingBody({ includeAll: true }),
          sortBy: null,
          sortDirection: null,
          clubUuid: null,
          type
        };

        return firstValueFrom(this.http.post<any>(GET_PASSES(show), payload))
          .then((response) => parseGetPasses(response))
          // Pass types the user can't access fail silently.
          .catch(() => []);
      })
    );

    const allPasses = results.flat();
    if (allPasses.length > 0) {
      await this.storeObjects(this.keyedRows(allPasses, 'passUuid', this.passesStoreName), this.passesStoreName);
    }
  }

  /** Pull the rows out of whichever envelope shape the API answered with. */
  private toRowArray(response: unknown, storeName: string): Record<string, unknown>[] {
    const data = Array.isArray(response) ? response : ((response as { data?: unknown })?.data ?? response);

    if (Array.isArray(data)) {
      return data as Record<string, unknown>[];
    }

    const items = (data as { items?: unknown })?.items;
    if (Array.isArray(items)) {
      return items as Record<string, unknown>[];
    }

    if (data) {
      console.error(`[IndexedDb] "${storeName}" not cached: response carries no row array.`);
    }
    return [];
  }

  /**
   * Populate the divisions and classes stores from the show's catalogue, which
   * returns one dictionary holding a "Divisions" and a "Classes" result set.
   * Both are keyed by `id`.
   *
   * This runs at login, before any entry exists, so the catalogue service is
   * asked for the entry-less form and falls back to the legacy endpoint if the
   * validation one requires an `entryUuid`.
   */
  private loadAllDivisionsAndClasses(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.catalog.load(this.currentShow()).subscribe({
        next: (data) => {
          // Dictionary keys keep their server-side casing; accept either so a
          // future naming policy on the API cannot silently empty the cache.
          const divisions = this.toRows(data['Divisions'] ?? data['divisions']);
          const classes = this.toRows(data['Classes'] ?? data['classes']);
          // Additional-Info field definitions, keyed by the `Id` a saved line
          // item references as its `LineItemFieldId`. Carries the labels the
          // Additional Information dialog renders.
          const fields = this.toRows(data['LineItemsFormFields'] ?? data['lineItemsFormFields']);

          this.storeObjects(
            this.keyedRows(divisions, 'divisionUuid', this.divisionsStoreName),
            this.divisionsStoreName
          )
            .then(() =>
              this.storeObjects(
                this.keyedRows(classes, 'classUuid', this.classesStoreName),
                this.classesStoreName
              )
            )
            .then(() =>
              this.storeObjects(
                this.keyedRows(fields, 'id', this.lineItemFieldsStoreName),
                this.lineItemFieldsStoreName
              )
            )
            .then(() => resolve())
            .catch(reject);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  /**
   * Normalise a GetShowsDivisions result set to camelCase.
   *
   * Those rows are built as `Dictionary<string, object>` from the stored
   * procedure's own column names, and System.Text.Json applies no naming policy
   * to dictionary keys — so they arrive PascalCase (`ClassUuid`, `Description`)
   * while the rest of the app expects camelCase.
   */
  private toRows(rows: unknown): Record<string, unknown>[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((row) => {
      const normalised: Record<string, unknown> = {};
      Object.keys(row ?? {}).forEach((key) => {
        normalised[toCamelCase(key)] = row[key];
      });
      return normalised;
    });
  }

  /**
   * Normalize UUID keys to lowercase to enable fast exact-match lookups.
   * This prevents the need for expensive case-insensitive scans.
   *
   * @param obj The object to normalize
   * @param storeName The store name to determine which UUID keys to normalize
   * @returns Object with normalized UUID keys
   */
  private normalizeUuidKeys(obj: any, storeName: string): any {
    if (!obj) return obj;

    const normalized = { ...obj };

    if (storeName === this.clubsStoreName && normalized.clubUuid) {
      normalized.clubUuid = normalized.clubUuid.toLowerCase();
    } else if (storeName === this.classesStoreName && normalized.classUuid) {
      normalized.classUuid = normalized.classUuid.toLowerCase();
    } else if (storeName === this.passesStoreName && normalized.passUuid) {
      normalized.passUuid = normalized.passUuid.toLowerCase();
    } else if (storeName === this.divisionsStoreName && normalized.divisionUuid) {
      normalized.divisionUuid = normalized.divisionUuid.toLowerCase();
    }

    return normalized;
  }

  /**
   * Keep only rows carrying the store's key path. A result set missing it
   * cannot be written at all, so name the columns that are present instead of
   * letting IndexedDB fail with an opaque DataError.
   */
  private keyedRows(
    rows: Record<string, unknown>[],
    keyPath: string,
    storeName: string
  ): Record<string, unknown>[] {
    const keyed = rows.filter((row) => row[keyPath] !== undefined && row[keyPath] !== null);

    if (rows.length && !keyed.length) {
      console.error(
        `[IndexedDb] "${storeName}" not cached: response rows carry no "${keyPath}". ` +
          `Columns present: ${Object.keys(rows[0]).join(', ')}`
      );
    }

    return keyed;
  }

  // ==================== GETTERS ====================

  async getClubDetailsById(clubUuid: string): Promise<ClubDetails | undefined> {
    await this.ensureDbConnection();

    if (!this.db || !clubUuid) {
      return undefined;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.clubsStoreName], 'readonly');
      const store = transaction.objectStore(this.clubsStoreName);
      // Normalize to lowercase for consistent lookup
      const request = store.get(clubUuid.toLowerCase());

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          const { timestamp, ...clubDetails } = result;
          resolve(clubDetails as ClubDetails);
        } else {
          resolve(undefined);
        }
      };

      request.onerror = () => {
        console.error('Failed to get club by id:', clubUuid);
        resolve(undefined);
      };
    });
  }

  async getClubNameById(clubUuid: string): Promise<string> {
    await this.ensureDbConnection();

    if (!this.db || !clubUuid) {
      return '';
    }

    await this.clubsLoad;
    const cached = await this.readClubName(clubUuid);
    if (cached) {
      return cached;
    }

    await this.reseedClubs();
    return this.readClubName(clubUuid);
  }

  private readClubName(clubUuid: string): Promise<string> {
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.clubsStoreName], 'readonly');
      const store = transaction.objectStore(this.clubsStoreName);
      // Normalize to lowercase for consistent lookup
      const request = store.get(clubUuid.toLowerCase());

      request.onsuccess = () => {
        const result = request.result;
        if (result && (result as ClubDetails).clubName) {
          resolve((result as ClubDetails).clubName);
        } else {
          resolve('');
        }
      };

      request.onerror = () => {
        console.error('Failed to get club by id:', clubUuid);
        resolve('');
      };
    });
  }

  /**
   * Every club, sorted by name — the one source for club dropdowns and name
   * lookups. Waits out the login seed, and refetches once if the store is empty.
   */
  async getAllClubs(): Promise<ClubDetails[]> {
    await this.ensureDbConnection();

    if (!this.db) {
      return [];
    }

    await this.clubsLoad;
    const clubs = await this.readAllClubs();
    if (clubs.length) {
      return clubs;
    }

    await this.reseedClubs();
    return this.readAllClubs();
  }

  /**
   * Club dropdown options. `Master/GetClubs`'s flags are plain subset filters
   * over one global list, so they are applied here instead of a request each.
   */
  async getClubOptions(filter?: ClubFlagFilter): Promise<ClubInfo[]> {
    return (await this.getAllClubs())
      .map((club) => ({
        ...club,
        isFourHFlag: club.isFourHFlag ?? false,
        isFFAFlag: club.isFFAFlag ?? false,
        isAstCeaFlag: (club as { isAstCeaFlag?: boolean }).isAstCeaFlag ?? false
      }))
      .filter(
        (club) =>
          (!filter?.isFourHFlag || club.isFourHFlag) &&
          (!filter?.isFFAFlag || club.isFFAFlag) &&
          (!filter?.isAstCeaFlag || club.isAstCeaFlag) &&
          club.isActive !== false
      );
  }

  /** Re-pull the clubs store after a club is created, edited or re-flagged. */
  refreshClubs(): Promise<void> {
    this.clubsLoad = this.loadAllClubDetails(true).catch(() => undefined);
    return this.clubsLoad;
  }

  private reseedClubs(): Promise<void> {
    this.clubReseed ??= this.loadAllClubDetails().catch(() => undefined);
    return this.clubReseed;
  }

  private async clearStore(storeName: string): Promise<void> {
    await this.ensureDbConnection();
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([storeName], 'readwrite');
      transaction.objectStore(storeName).clear();
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
    });
  }

  private readAllClubs(): Promise<ClubDetails[]> {
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.clubsStoreName], 'readonly');
      const store = transaction.objectStore(this.clubsStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          const clubs = (result as ClubDetails[]).sort((a, b) =>
            (a.clubName ?? '').localeCompare(b.clubName ?? '')
          );
          resolve(clubs);
        } else {
          resolve([]);
        }
      };

      request.onerror = () => {
        resolve([]);
      };
    });
  }

  async getAllClasses(): Promise<Class[]> {
    await this.ensureDbConnection();

    if (!this.db) {
      return [];
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.classesStoreName], 'readonly');
      const store = transaction.objectStore(this.classesStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // Stored-procedure columns are nullable, so sort defensively.
          const classes = (result as Class[]).sort((a, b) =>
            (a.description ?? '').localeCompare(b.description ?? '')
          );
          resolve(classes);
        } else {
          resolve([]);
        }
      };

      request.onerror = () => {
        resolve([]);
      };
    });
  }

  async getClassNameById(classUuid: string): Promise<string> {
    await this.ensureDbConnection();

    if (!this.db || !classUuid) {
      return '';
    }

    const cached = await this.readCachedName(this.classesStoreName, classUuid, 'class');
    if (cached) {
      return cached;
    }

    // A miss can also mean the store never populated at all: login-time seeding
    // runs behind `Promise.allSettled`, so a catalogue failure there is silent
    // and leaves every caller printing a raw uuid. Re-seed once per session and
    // look again before giving up.
    await this.reseedDivisionsAndClasses();
    return this.readCachedName(this.classesStoreName, classUuid, 'class');
  }

  /** One store lookup, keyed by lowercased uuid. */
  private readCachedName(storeName: string, uuid: string, nameKey: 'class' | 'pass'): Promise<string> {
    return new Promise((resolve) => {
      const store = this.db!.transaction([storeName], 'readonly').objectStore(storeName);
      const request = store.get(uuid.toLowerCase());

      request.onsuccess = () => resolve(cachedName(request.result, nameKey));
      request.onerror = () => {
        console.error(`Failed to read "${storeName}" by id:`, uuid);
        resolve('');
      };
    });
  }

  /** Memoised so a page full of unresolved uuids costs one refetch, not one each. */
  private reseedDivisionsAndClasses(): Promise<void> {
    this.divisionReseed ??= this.loadAllDivisionsAndClasses().catch(() => undefined);
    return this.divisionReseed;
  }

  /** Same, for the passes store. */
  private reseedPasses(): Promise<void> {
    this.passReseed ??= this.loadAllPasses().catch(() => undefined);
    return this.passReseed;
  }

  async getAllDivisions(): Promise<Division[]> {
    await this.ensureDbConnection();

    if (!this.db) {
      return [];
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.divisionsStoreName], 'readonly');
      const store = transaction.objectStore(this.divisionsStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        const result = request.result;
        resolve(result as Division[] || []);
      };

      request.onerror = () => {
        resolve([]);
      };
    });
  }

  /**
   * Additional-Info field labels, keyed by settingsId. A per-class override in
   * `customLabelName` wins over the shared `labelName`.
   *
   * Note: whether an entry line item's `lineItemFieldId` refers to `settingsId`
   * or to the settings row's own `id` is still unconfirmed, so both are mapped.
   * Distinct ranges mean the two sets do not currently collide.
   */
  async getLineItemFieldLabels(): Promise<Map<number, string>> {
    await this.ensureDbConnection();
    const labels = new Map<number, string>();

    if (!this.db) {
      return labels;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.lineItemFieldsStoreName], 'readonly');
      const store = transaction.objectStore(this.lineItemFieldsStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        (request.result ?? []).forEach((field: Record<string, unknown>) => {
          // LineItemsFormFields uses `label`; the others are the older
          // DivisionClassSettings names, kept so a stale cache still reads.
          const label = (field['label'] || field['customLabelName'] || field['labelName']) as string;
          if (!label) {
            return;
          }
          [field['settingsId'], field['id']].forEach((key) => {
            const id = Number(key);
            if (!Number.isNaN(id) && !labels.has(id)) {
              labels.set(id, label);
            }
          });
        });
        resolve(labels);
      };

      request.onerror = () => resolve(labels);
    });
  }

  /**
   * Additional-Info field types, keyed the same way as
   * {@link getLineItemFieldLabels} — the entry lists read it to tell an uploaded
   * photo (whose stored value is a blob uuid) from ordinary text.
   */
  async getLineItemFieldTypes(): Promise<Map<number, string>> {
    await this.ensureDbConnection();
    const types = new Map<number, string>();

    if (!this.db) {
      return types;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.lineItemFieldsStoreName], 'readonly');
      const store = transaction.objectStore(this.lineItemFieldsStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        (request.result ?? []).forEach((field: Record<string, unknown>) => {
          const fieldType = (field['fieldType'] || field['type']) as string;
          if (!fieldType) {
            return;
          }
          [field['settingsId'], field['id']].forEach((key) => {
            const id = Number(key);
            if (!Number.isNaN(id) && !types.has(id)) {
              types.set(id, fieldType);
            }
          });
        });
        resolve(types);
      };

      request.onerror = () => resolve(types);
    });
  }

  /** Look a division up by its uuid, the store's key. */
  async getDivisionByUuid(divisionUuid: string): Promise<Division | undefined> {
    await this.ensureDbConnection();

    if (!this.db || !divisionUuid) {
      return undefined;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.divisionsStoreName], 'readonly');
      const store = transaction.objectStore(this.divisionsStoreName);
      // Normalize to lowercase for consistent lookup
      const request = store.get(divisionUuid.toLowerCase());

      request.onsuccess = () => resolve((request.result as Division) ?? undefined);
      request.onerror = () => {
        console.error('Failed to get division by uuid:', divisionUuid);
        resolve(undefined);
      };
    });
  }

  async getAllPasses(): Promise<Pass[]> {
    await this.ensureDbConnection();

    if (!this.db) {
      return [];
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.passesStoreName], 'readonly');
      const store = transaction.objectStore(this.passesStoreName);
      const request = store.getAll();

      request.onsuccess = () => {
        const result = request.result;
        resolve(result as Pass[] || []);
      };

      request.onerror = () => {
        resolve([]);
      };
    });
  }

  async getPassNameById(passUuid: string): Promise<string> {
    await this.ensureDbConnection();

    if (!this.db || !passUuid) {
      return '';
    }

    const cached = await this.readCachedName(this.passesStoreName, passUuid, 'pass');
    if (cached) {
      return cached;
    }

    // As with classes: the passes endpoint is not deployed on every show, so a
    // login-time failure here is swallowed and the store can be empty.
    await this.reseedPasses();
    return this.readCachedName(this.passesStoreName, passUuid, 'pass');
  }

  /**
   * Which slice of the pass catalogue a set of passes belongs to, as the pass
   * screen names them: `educator` (AST/CEA), `premium` or `exhibitor`. Returns
   * null when none of the uuids are cached, so the caller can pick its own
   * fallback rather than opening the wrong slice.
   */
  async getPassTypeFromPassIds(passUuids: string[]): Promise<string | null> {
    await this.ensureDbConnection();

    if (!this.db || !passUuids.length) {
      return null;
    }

    const passes = await Promise.all(passUuids.map((uuid) => this.getPassByUuid(uuid)));
    const found = passes.filter((pass): pass is Pass => !!pass);

    if (!found.length) {
      return null;
    }

    if (found.some((pass) => pass.astCea)) return 'educator';
    if (found.some((pass) => pass.premium)) return 'premium';
    return 'exhibitor';
  }

  private getPassByUuid(passUuid: string): Promise<Pass | undefined> {
    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.passesStoreName], 'readonly');
      const store = transaction.objectStore(this.passesStoreName);
      // Normalize to lowercase for consistent lookup
      const request = store.get(passUuid.toLowerCase());

      request.onsuccess = () => {
        if (!request.result) {
          resolve(undefined);
          return;
        }
        const { timestamp, ...pass } = request.result;
        resolve(pass as Pass);
      };

      request.onerror = () => resolve(undefined);
    });
  }

  async getOrgConfigsById(orgId: number): Promise<OrgConfigs | undefined> {
    await this.ensureDbConnection();

    if (!this.db) {
      return undefined;
    }

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.orgConfigsStoreName], 'readonly');
      const store = transaction.objectStore(this.orgConfigsStoreName);
      const request = store.get(orgId);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          const { timestamp, ...orgConfigs } = result;
          resolve(orgConfigs as OrgConfigs);
        } else {
          resolve(undefined);
        }
      };

      request.onerror = () => {
        resolve(undefined);
      };
    });
  }

  /**
   * Clear database on logout
   */
  clearDatabase(): Promise<void> {
    this.catalog.clearCache();
    return this.clearAllData();
  }
}
